// App shell — plan §14.5: 240px sabit sidebar + 1fr content + sticky top bar (§4.8).
// Sol sidebar: brand + nav. Sağ top bar: breadcrumb + avatar dropdown (Çıkış).
// 2026-04-24: Lucide icon + collapse toggle (rail 60px). app-store.sidebarCollapsed persist.
// Finar UX P3 K1 (TODO-UX-06): ≤1024 px menü kendiliğinden raya düşer, ☰ açılır menüyü (overlay)
// açar; <768 px ray da gizlenir. Kurallar lib/shell-layout.ts'te; menü AppShellNav'da.

import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { roleDisplayName } from "@mahir/shared";
import { useAuthStore } from "../../stores/auth-store";
import { useAppStore } from "../../stores/app-store";
import { useMediaQuery } from "../../hooks/use-media-query";
import { profileButtonLabel } from "../../lib/shell-a11y";
import {
  HIDDEN_RAIL_QUERY,
  NARROW_QUERY,
  SHELL_COPY,
  railCollapsed,
  shellMode,
  showMenuButton,
  sidebarColumn,
} from "../../lib/shell-layout";
import { computeCrumbs } from "../../lib/breadcrumbs";
import { useBreadcrumbStore } from "../../stores/breadcrumb-store";
import { NotificationBell } from "./NotificationBell";
import { QueuePauseBanner } from "./QueuePauseBanner";
import { SidebarNav } from "./AppShellNav";
import { APP_SHELL_DRAWER_ID, AppShellDrawer } from "./AppShellDrawer";

export function AppShell(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const preferCollapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const mode = shellMode(useMediaQuery(NARROW_QUERY), useMediaQuery(HIDDEN_RAIL_QUERY));
  const collapsed = railCollapsed(mode, preferCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerButtonRef = useRef<HTMLButtonElement | null>(null);
  const railRef = useRef<HTMLElement | null>(null);
  const mainColumnRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const wasDrawerOpen = useRef(false);
  const focusMainOnClose = useRef(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  // Finar rev-A 15 — sayfa kendi detay etiketini verir (ör. müşteri adı); yoksa "Detay".
  const crumbDetail = useBreadcrumbStore((s) => s.detail);

  // Açılır menü: rota değişince (odak yeni sayfanın içeriğine) ya da geniş ekrana geçince
  // (☰ kalkar → odak içeriğe) kapanır.
  useEffect(() => {
    if (wasDrawerOpen.current) focusMainOnClose.current = true;
    setDrawerOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!showMenuButton(mode)) setDrawerOpen(false);
  }, [mode]);

  // Açıkken arka plan `inert` (odak/okuyucu menüde kalır) — kip değişiminde sonradan mount
  // olan ray da kapsanır. Kapanınca odak ☰'ye, ☰ yoksa ya da rota değiştiyse içeriğe döner.
  useEffect(() => {
    for (const el of [railRef.current, mainColumnRef.current]) {
      if (el) el.inert = drawerOpen;
    }
    if (!drawerOpen && wasDrawerOpen.current) {
      const toMain = focusMainOnClose.current || drawerButtonRef.current === null;
      (toMain ? mainRef.current : drawerButtonRef.current)?.focus({ preventScroll: true });
    }
    focusMainOnClose.current = false;
    wasDrawerOpen.current = drawerOpen;
  }, [drawerOpen, mode]);

  // Outside click → menu close
  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  if (!user) return <></>;

  const crumbs = computeCrumbs(
    location.pathname,
    crumbDetail?.path === location.pathname ? crumbDetail.label : null,
  );

  const avatarText = user.fullName
    .split(" ")
    .map((s) => s[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const sidebarWidth = sidebarColumn(mode, preferCollapsed);

  return (
    <div
      className="min-h-screen grid"
      style={{
        gridTemplateColumns:
          sidebarWidth === null ? "minmax(0, 1fr)" : `${sidebarWidth} minmax(0, 1fr)`,
        background: "var(--color-bg)",
        transition: "grid-template-columns 180ms ease",
      }}
    >
      {/* Sidebar — brand + toggle + nav.
       * Palet v2'de Dark açık ton olduğu için text/border var(--color-text) +
       * primary-derived olarak ayarlandı (kontrast WCAG AA). v1 (koyu Dark) için
       * brand/nav text'i var(--color-surface) idi; rollback'te birlikte revert.
       * Dar ekranda (rail) elle daraltma düğmesi yok — menüyü üst bardaki ☰ açar. */}
      {sidebarWidth !== null ? (
        <aside
          ref={railRef}
          data-testid="app-shell-sidebar"
          data-shell-mode={mode}
          className="flex flex-col"
          style={{
            background: "var(--color-dark)",
            color: "var(--color-text)",
            minHeight: "100vh",
            borderRight: "1px solid var(--color-border)",
            minWidth: 0,
          }}
        >
          {mode === "wide" ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "space-between",
                padding: collapsed ? "20px 0 24px" : "20px 18px 24px",
              }}
            >
              {!collapsed && (
                <img
                  src="/Mahir_logo.png"
                  alt="Mahir AI"
                  style={{ height: 44, width: "auto", display: "block" }}
                />
              )}
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label={collapsed ? SHELL_COPY.openMenu : SHELL_COPY.collapseMenu}
                title={collapsed ? SHELL_COPY.openMenu : SHELL_COPY.collapseMenu}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text-2)",
                  cursor: "pointer",
                }}
              >
                {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
              </button>
            </div>
          ) : (
            <div style={{ height: 20 }} aria-hidden="true" />
          )}

          <SidebarNav
            collapsed={collapsed}
            permissions={user.permissions}
            pathname={location.pathname}
          />
        </aside>
      ) : null}

      {/* Main — sticky top bar + content.
       * `--layout-shell-header`: bu shell'in üst çubuk yüksekliği. Tam-yükseklik
       * isteyen sayfalar (FormFill 3-kolon çalışma alanı) `calc(100vh - var(...))`
       * ile kendi yüksekliğini buradan türetir — sayı iki yerde tekrar etmez. */}
      <div
        ref={mainColumnRef}
        className="flex flex-col min-h-screen min-w-0"
        style={
          { "--layout-shell-header": "var(--layout-topbar-height, 56px)" } as React.CSSProperties
        }
      >
        <header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            height: "var(--layout-topbar-height, 56px)",
            background: "var(--color-surface)",
            borderBottom: "1px solid var(--color-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: mode === "hidden" ? "0 16px" : "0 24px",
          }}
        >
          {showMenuButton(mode) ? (
            <button
              ref={drawerButtonRef}
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label={SHELL_COPY.openMenu}
              title={SHELL_COPY.openMenu}
              aria-expanded={drawerOpen}
              aria-controls={APP_SHELL_DRAWER_ID}
              data-testid="app-shell-menu-button"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-2)",
                cursor: "pointer",
              }}
            >
              <Menu size={18} aria-hidden="true" />
            </button>
          ) : null}
          <nav
            aria-label="breadcrumb"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "0.85rem",
              color: "var(--color-text-2)",
              minWidth: 0,
              flex: 1,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              return (
                <span key={`${c}-${i}`} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {i > 0 ? <span style={{ color: "var(--color-text-3)" }}>/</span> : null}
                  <span
                    style={{
                      color: last ? "var(--color-text)" : "var(--color-text-3)",
                      fontWeight: last ? 500 : 400,
                    }}
                  >
                    {c}
                  </span>
                </span>
              );
            })}
          </nav>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <NotificationBell />
            <div ref={menuRef} style={{ position: "relative" }}>
              <button
                onClick={() => setMenuOpen((s) => !s)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                // İ5 — dar ekranda ad gizlenir, avatar aria-hidden: erişilebilir ad buradan.
                aria-label={profileButtonLabel(user)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "4px 10px 4px 4px",
                  borderRadius: 999,
                  border: "1px solid var(--color-border)",
                  background: menuOpen ? "rgba(15,42,61,0.04)" : "var(--color-bg)",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  color: "var(--color-text)",
                  fontFamily: "var(--font-sans)",
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "var(--color-primary)",
                    color: "var(--color-surface)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    fontFamily: "var(--font-sans)",
                  }}
                  aria-hidden="true"
                >
                  {avatarText}
                </span>
                <span className="hidden md:inline">
                  {user.fullName} ·{" "}
                  <span style={{ color: "var(--color-text-3)" }}>
                    {roleDisplayName(user.roleName)}
                  </span>
                </span>
                <span aria-hidden="true" style={{ color: "var(--color-text-3)", fontSize: 10 }}>
                  ▾
                </span>
              </button>
              {menuOpen ? (
                <div
                  role="menu"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 6px)",
                    minWidth: 220,
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    boxShadow: "var(--shadow-md)",
                    padding: 6,
                    zIndex: 20,
                  }}
                >
                  <div
                    style={{
                      padding: "8px 10px 10px",
                      borderBottom: "1px solid var(--color-border)",
                      marginBottom: 4,
                    }}
                  >
                    <div style={{ fontSize: "0.85rem", color: "var(--color-text)" }}>
                      {user.fullName}
                    </div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "var(--color-text-3)",
                        fontFamily: "var(--font-mono)",
                        marginTop: 2,
                      }}
                    >
                      {user.email}
                    </div>
                  </div>
                  <button
                    role="menuitem"
                    onClick={() => void logout()}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 10px",
                      borderRadius: 6,
                      background: "transparent",
                      border: "none",
                      color: "var(--color-error)",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(198,40,40,0.06)")
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    Çıkış
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <QueuePauseBanner />

        {/* Content */}
        <main
          ref={mainRef}
          tabIndex={-1}
          className="flex-1 overflow-auto"
          style={{ minWidth: 0, outline: "none" }}
        >
          <Outlet />
        </main>
      </div>
      {drawerOpen ? (
        <AppShellDrawer
          permissions={user.permissions}
          pathname={location.pathname}
          onClose={closeDrawer}
        />
      ) : null}
    </div>
  );
}
